import { db, type Review, type Character, type Session } from "./db";
import { getNow } from "$lib/stores/adminTime";

export type MasteryLevel =
	| "mastered"
	| "good"
	| "learning"
	| "difficult"
	| "new";

export function getMasteryLevel(review: Review | undefined): MasteryLevel {
	if (!review) return "new";
	if (review.srsMethod === "fsrs") {
		// FSRS: use stability as the mastery measure
		const stability = review.stability ?? 0;
		const state = review.fsrsState ?? 0;
		if (state === 0) return "new"; // State.New
		if (stability >= 21) return "mastered";
		if (stability >= 6) return "good";
		if (stability >= 1) return "learning";
		return "difficult";
	}
	// SM-2
	if (review.repetitions === 0) return "difficult";
	if (review.interval >= 21) return "mastered";
	if (review.interval >= 6) return "good";
	if (review.interval >= 1) return "learning";
	return "difficult";
}

// Minimum lifetime accuracy (correct ÷ total attempts, %) for a script to count
// as mastered.
export const SCRIPT_MASTERY_ACCURACY = 90;

// A character is "studied" (for script-mastery purposes) once it has reached
// the `mastered` level (interval >= 21 for SM-2 / stability >= 21 for FSRS).
function isReviewStudied(review: Review | undefined): boolean {
	return getMasteryLevel(review) === "mastered";
}

export function getMasteryColour(level: MasteryLevel): string {
	switch (level) {
		case "mastered":
			return "var(--color-success)";
		case "good":
			return "var(--accent-light-green)";
		case "learning":
			return "var(--color-warning)";
		case "difficult":
			return "var(--color-error)";
		case "new":
			return "var(--mastery-new)";
	}
}

export interface DashboardStats {
	learnt: number;
	accuracy: number;
	dueToday: number;
}

export async function getDashboardStats(
	script: string,
): Promise<DashboardStats> {
	const reviews = await db.reviews.where("script").equals(script).toArray();

	// For FSRS, "learnt" = has been reviewed at least once (state > New)
	// For SM-2, "learnt" = repetitions > 0
	const learnt = reviews.filter((r) => {
		if (r.srsMethod === "fsrs") return (r.fsrsState ?? 0) > 0;
		return r.repetitions > 0;
	}).length;

	const reviewed = reviews.filter((r) => {
		if (r.srsMethod === "fsrs") return (r.fsrsState ?? 0) > 0;
		return r.repetitions > 0;
	});
	const accurate = reviewed.filter((r) => {
		if (r.srsMethod === "fsrs") return (r.difficulty ?? 10) <= 5;
		return r.easeFactor >= 2.5;
	}).length;
	const accuracy =
		reviewed.length > 0
			? Math.round((accurate / reviewed.length) * 100)
			: 0;

	const { getSrsMethod } = await import("./scripts");
	const srsMethod = await getSrsMethod(script);
	const { getDueForScript } = await import("./srs-router");
	const dueCards = await getDueForScript(script, db, srsMethod);
	const dueToday = dueCards.length;

	return { learnt, accuracy, dueToday };
}

export interface CharacterGridItem {
	character: string;
	reading: string;
	mastery: MasteryLevel;
}

export async function getCharacterGrid(
	script: string,
): Promise<CharacterGridItem[]> {
	const characters = await db.characters
		.where("script")
		.equals(script)
		.toArray();
	const reviews = await db.reviews.where("script").equals(script).toArray();
	const reviewMap = new Map<string, Review>();
	for (const r of reviews) {
		reviewMap.set(r.itemId, r);
	}

	return characters.map((c: Character) => ({
		character: c.character,
		reading: c.readings?.[0] ?? c.meaning,
		mastery: getMasteryLevel(reviewMap.get(c.id)),
	}));
}

export interface MasteryBreakdown {
	mastered: number;
	good: number;
	learning: number;
	difficult: number;
	new: number;
}

export interface ScriptStudyState {
	script: string;
	label: string;
	grafemsToLearn: string[]; // glyphIds — lesson-ordered, capped to remaining daily quota
	grafemsToReview: string[]; // glyphIds — due for review right now
	masteryBreakdown: MasteryBreakdown;
}

export async function getMasteryBreakdown(
	script: string,
): Promise<MasteryBreakdown> {
	const characters = await db.characters
		.where("script")
		.equals(script)
		.toArray();
	const reviews = await db.reviews.where("script").equals(script).toArray();
	const reviewMap = new Map<string, Review>();
	for (const r of reviews) {
		reviewMap.set(r.itemId, r);
	}

	const breakdown: MasteryBreakdown = {
		mastered: 0,
		good: 0,
		learning: 0,
		difficult: 0,
		new: 0,
	};
	for (const c of characters) {
		const level = getMasteryLevel(reviewMap.get(c.id));
		breakdown[level]++;
	}
	return breakdown;
}

export interface ScriptProgressItem {
	script: string;
	label: string;
	percentage: number;
	total: number;
	learnt: number;
	// Unique characters that have reached at least the `learning` level. Used,
	// together with accuracy, to decide whether a script is mastered.
	studied: number;
}

export async function getScriptProgress(): Promise<ScriptProgressItem[]> {
	const { getAvailableScripts } = await import("./scripts");
	const defs = await getAvailableScripts();
	const results: ScriptProgressItem[] = [];

	for (const def of defs) {
		const total = await db.characters
			.where("script")
			.equals(def.id)
			.count();
		if (total === 0) continue;
		const reviews = await db.reviews
			.where("script")
			.equals(def.id)
			.toArray();
		// Count unique characters (itemIds) that have been reviewed.
		// For FSRS: state > New. For SM-2: repetitions > 0.
		const learnt = new Set(
			reviews.filter((r) => {
				if (r.srsMethod === "fsrs") return (r.fsrsState ?? 0) > 0;
				return r.repetitions > 0;
			}).map((r) => r.itemId),
		).size;
		const studied = Math.min(
			total,
			new Set(reviews.filter(isReviewStudied).map((r) => r.itemId)).size,
		);
		const percentage = Math.min(100, Math.round((learnt / total) * 100));
		results.push({
			script: def.id,
			label: def.name,
			percentage,
			total,
			learnt,
			studied,
		});
	}

	return results;
}

export async function getProgressForScript(
	scriptId: string,
): Promise<ScriptProgressItem | null> {
	const total = await db.characters.where("script").equals(scriptId).count();
	if (total === 0) return null;
	const reviews = await db.reviews.where("script").equals(scriptId).toArray();
	const learnt = reviews.filter((r) => {
		if (r.srsMethod === "fsrs") return (r.fsrsState ?? 0) > 0;
		return r.repetitions > 0;
	}).length;
	const studied = Math.min(
		total,
		new Set(reviews.filter(isReviewStudied).map((r) => r.itemId)).size,
	);
	const percentage = Math.round((learnt / total) * 100);
	let label = scriptId;
	try {
		const { getScript } = await import("./scripts");
		const def = await getScript(scriptId);
		label = def.name;
	} catch {
		// keep scriptId as label
	}
	return { script: scriptId, label, percentage, total, learnt, studied };
}

/**
 * Fetch lifetime accuracy (correct ÷ total attempts, %) per script for the
 * current user. Accuracy lives only on the server, so this hits the API.
 * Returns a map of scriptId → accuracy (0–100).
 */
export async function getScriptAccuracy(): Promise<Record<string, number>> {
	const { api } = await import("./api");
	const rows = await api.progress.getAccuracy();
	const map: Record<string, number> = {};
	for (const row of rows) {
		map[row.script] = row.accuracy;
	}
	return map;
}

/**
 * A script is mastered when every character has reached the `mastered`
 * level and lifetime accuracy meets the threshold.
 */
export function isScriptMastered(
	progress: ScriptProgressItem | undefined,
	accuracy: number,
): boolean {
	if (!progress || progress.total === 0) return false;
	return (
		progress.studied === progress.total &&
		accuracy >= SCRIPT_MASTERY_ACCURACY
	);
}

export interface UpcomingReviewItem {
	character: string;
	reading: string;
	interval: number;
	dueIn: string;
}

export async function getUpcomingReviews(
	script: string,
	limit = 4,
): Promise<UpcomingReviewItem[]> {
	const now = getNow();
	const reviews = await db.reviews.where("script").equals(script).toArray();
	const upcoming = reviews
		.filter((r) => {
			if (r.srsMethod === "fsrs") return (r.fsrsState ?? 0) > 0;
			return r.repetitions > 0;
		})
		.sort(
			(a, b) =>
				new Date(a.nextReview).getTime() -
				new Date(b.nextReview).getTime(),
		)
		.slice(0, limit);

	const characters = await db.characters
		.where("script")
		.equals(script)
		.toArray();
	const charMap = new Map<string, Character>();
	for (const c of characters) {
		charMap.set(c.id, c);
	}

	return upcoming.map((r) => {
		const char = charMap.get(r.itemId);
		const due = new Date(r.nextReview);
		const diffMs = due.getTime() - now.getTime();
		let dueIn: string;
		if (diffMs <= 0) {
			dueIn = "Now";
		} else if (diffMs < 3600000) {
			dueIn = `${Math.round(diffMs / 60000)}m`;
		} else if (diffMs < 86400000) {
			dueIn = `${Math.round(diffMs / 3600000)}h`;
		} else {
			dueIn = `${Math.round(diffMs / 86400000)}d`;
		}

		return {
			character: char?.character ?? "?",
			reading: char?.readings?.[0] ?? char?.meaning ?? "",
			interval: r.interval,
			dueIn,
		};
	});
}

/**
 * Count cards due within the next `windowMinutes` for FSRS short-term scheduling.
 * Returns 0 for SM-2 scripts (no sub-day scheduling).
 */
export async function getDueSoonCount(
	script: string,
	windowMinutes = 60,
): Promise<number> {
	const now = getNow();
	const cutoff = new Date(now.getTime() + windowMinutes * 60_000).toISOString();
	const nowStr = now.toISOString();
	const reviews = await db.reviews
		.where("script")
		.equals(script)
		.and((r: Review) => {
			if (r.srsMethod !== "fsrs") return false;
			const state = r.fsrsState ?? 0;
			return state > 0 && r.nextReview > nowStr && r.nextReview <= cutoff;
		})
		.count();
	return reviews;
}

export interface WeeklyActivityItem {
	day: string;
	count: number;
}

export async function getWeeklyActivity(): Promise<WeeklyActivityItem[]> {
	const dayLabels = ["M", "T", "W", "T", "F", "S", "S"];
	const now = getNow();
	const today = now.getDay(); // 0=Sun, 1=Mon...
	const mondayOffset = today === 0 ? 6 : today - 1;

	const monday = new Date(now);
	monday.setDate(now.getDate() - mondayOffset);
	monday.setHours(0, 0, 0, 0);

	const sessions = await db.sessions.toArray();

	const result: WeeklyActivityItem[] = [];
	for (let i = 0; i < 7; i++) {
		const d = new Date(monday);
		d.setDate(monday.getDate() + i);
		const dateStr = d.toISOString().split("T")[0];

		const count = sessions
			.filter((s: Session) => s.date === dateStr)
			.reduce((sum: number, s: Session) => sum + s.cardsReviewed, 0);

		result.push({ day: dayLabels[i], count });
	}

	return result;
}
